import React, { FC, useState } from "react";

import classNames from "classnames";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { DailyTipWithFixture } from "@api";

import { formatOdds, formatProbability, formatTipName } from "@utils";

import {
  TeamsHeader,
  TeamsInfoItem,
  useTeamsHeaderProps,
} from "@ui/components";
import { Box, Button, Icon, Tag, Text, Title } from "@ui/elements";
import { Stack } from "@ui/layout";

import {
  MatchDetailsDrawer,
  ScreenshotButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components";

import { formatTipCategory } from "../../utils/formatTipCategory";

import "./TopPickBox.scss";

type TopPickBoxProps = DailyTipWithFixture;

export const TopPickBox: FC<TopPickBoxProps> = ({ fixture, tip }) => {
  const teamsHeaderProps = useTeamsHeaderProps(fixture);
  const [showFullReasoning, setShowFullReasoning] = useState(false);
  const [matchDetailsOpen, setMatchDetailsOpen] = useState(false);
  const { t } = useTranslation();
  const boxRef = React.useRef<HTMLDivElement | null>(null);

  return (
    <Box className="top-tip-box" ref={boxRef}>
      <MatchDetailsDrawer
        isOpen={matchDetailsOpen}
        onClose={() => setMatchDetailsOpen(false)}
        fixture={fixture}
      />
      <Stack direction="column">
        <Stack
          direction="row"
          justify="between"
          className="top-pick-box-header"
          align="center"
        >
          <Stack align="center">
            <div className="top-pick-box-header__rank">{tip.rank}</div>
            <Tag
              type={tip.type === "safe" ? "success" : "info"}
              variant="light"
            >
              {t(`rating.${tip.type}`)}
            </Tag>
          </Stack>
          <Stack gap="xxs" className="print-hidden">
            <ScreenshotButton
              variant="text"
              size="small"
              filename={`${tip.rank} - ${fixture.teams.home.name} vs ${fixture.teams.away.name}.png`}
              elementRef={boxRef}
              width={550}
              options={{
                type: "image/png",
                cacheBust: true,
              }}
            >
              <Icon icon="DownloadSimple" />
            </ScreenshotButton>
            <Button
              variant="text"
              size="small"
              onClick={() => setMatchDetailsOpen(true)}
            >
              <span>{t("labels.view-ai-analysis")}</span>
              <Icon icon="CaretRight" />
            </Button>
          </Stack>
        </Stack>
        <div
          className="teams-header-wrapper"
          onClick={() => setMatchDetailsOpen(true)}
        >
          <TeamsHeader
            {...teamsHeaderProps}
            subtitle={
              <>
                <TeamsInfoItem
                  icon="Alarm"
                  label={dayjs(fixture.fixture.date).format("HH:mm")}
                />
                <TeamsInfoItem icon="Trophy" label={fixture.league.name} />
              </>
            }
          />
        </div>

        <Stack
          justify="between"
          className={classNames("tip-recommendation-box")}
        >
          <Stack direction="column" gap="xxs">
            <Text variant="secondary">{formatTipCategory(tip.category)}</Text>
            <Title size={5} hasIcon>
              {formatTipName(tip.category, tip.name)}
              {tip.winner === true && (
                <Icon
                  icon="CheckCircle"
                  weight="fill"
                  className="has-text-success"
                />
              )}

              {tip.winner === false && (
                <Icon
                  icon="XCircle"
                  weight="fill"
                  className="has-text-danger"
                />
              )}
            </Title>
          </Stack>

          <Stack direction="column" gap="xxs" justify="end">
            <Text variant="secondary" align="right">
              {t("odds")}
            </Text>
            <Title size={5} align="right">
              {formatOdds(tip.odds)}
            </Title>
          </Stack>
        </Stack>

        <Stack direction="column" align="start" gap="xxs">
          <Text
            variant="secondary"
            className={classNames("top-pick-reasoning", {
              "line-clamp-2": !showFullReasoning,
            })}
          >
            {tip.reasoning}
          </Text>
          <a
            className="tip-reasoning-toggle print-hidden"
            onClick={() => {
              setShowFullReasoning(!showFullReasoning);
            }}
          >
            {showFullReasoning ? t("labels.show-less") : t("labels.show-more")}
          </a>
        </Stack>

        <Stack justify="between">
          <Text>
            <strong>{t("probability")}: </strong>
            <span>{formatProbability(tip.probability)}</span>
          </Text>
          <Text hasIcon>
            <strong>{t("Edge")}: </strong>
            {formatProbability(tip.edge)}
            <Tooltip placement="bottom-end">
              <TooltipTrigger>
                <Icon className="print-hidden" icon="Info" size="small" />
              </TooltipTrigger>
              <TooltipContent>
                <Box className="tooltip-box">
                  <Text>{t("edgeInfo")}</Text>
                </Box>
              </TooltipContent>
            </Tooltip>
          </Text>
        </Stack>
      </Stack>
    </Box>
  );
};
